import React, { useState, useEffect } from 'react';
import {
	Box,
	Card,
	CardContent,
	Typography,
	Grid,
	Chip,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Paper,
	Alert,
	CircularProgress,
	Button,
	Divider,
	Stack,
	Avatar,
	TextField,
	Switch,
	FormControlLabel,
	Select,
	MenuItem,
	FormControl,
	Autocomplete,
	IconButton,
	Snackbar,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { es } from 'date-fns/locale';
import {
	ArrowBack,
	CheckCircle,
	Cancel,
	Public,
	LocationOn,
	CalendarToday,
	AttachMoney,
	Description,
	Category,
	Business,
	AccessTime,
	Settings,
	Edit,
	Save,
	Close,
	Add,
	Delete,
} from '@mui/icons-material';
import { useParams, useLocation, useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { isOperatorOrHigher } from 'features/auth/helpers';
import {
	getPrechargedServiceById,
	getServiceTypes,
	createPrechargedService,
	updatePrechargedService,
	getCities,
} from '../../services';
import { getProviderList, getProviderById } from '../../../providers/services';
import { useDebounceState } from 'features/common/hooks/useDebounceState';
import { NetsuiteSyncStatusEnum } from 'features/common/types';
import {
	IPrechargedService,
	IPrechargedServiceFare,
	IServiceType,
	ICity,
	getServiceTypeName,
	getServiceOptionName,
	SERVICE_OPTIONS_MAP,
} from '../../types';
import { IProviderListDTO, IProviderDTO } from '../../../providers/types';

const PrechargedServiceDetail: React.FC = () => {
	const { id } = useParams<{ id: string }>();
	const history = useHistory();
	const location = useLocation();
	const { auth } = useSelector((state: RootState) => state);

	// Detectar si estamos en modo de creación
	const isCreateMode = id === 'new';

	const [service, setService] = useState<IPrechargedService | null>(null);
	const [serviceTypes, setServiceTypes] = useState<IServiceType[]>([]);
	const [cities, setCities] = useState<ICity[]>([]);
	const [providers, setProviders] = useState<IProviderListDTO[]>([]);
	const [selectedProvider, setSelectedProvider] = useState<IProviderDTO | null>(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);

	// Estados para búsqueda de proveedores
	const [providerSearchValue, setProviderSearchValue] = useState<string>('');
	const [providersLoading, setProvidersLoading] = useState(false);
	const debouncedProviderSearch = useDebounceState(providerSearchValue, 300);

	// Estados para edición
	const [isEditing, setIsEditing] = useState(false);
	const [editedService, setEditedService] = useState<IPrechargedService | null>(null);
	const [savingChanges, setSavingChanges] = useState(false);
	const [saveAlert, setSaveAlert] = useState<{ show: boolean; severity: 'success' | 'error'; message: string }>({
		show: false,
		severity: 'success',
		message: '',
	});

	// Estados para edición de tarifas
	const [editingFareId, setEditingFareId] = useState<number | null>(null);

	// Verificar si el usuario puede editar
	const canEdit = isOperatorOrHigher(auth.user);

	// Función para cargar proveedores con búsqueda
	const loadProviders = async (search = '') => {
		try {
			setProvidersLoading(true);
			const providersData = await getProviderList(1, 50, undefined, search);
			let providersList = providersData.data.elements;

			// Si hay un proveedor seleccionado y no está en los resultados, agregarlo
			if (selectedProvider && !providersList.find((p) => p.id === selectedProvider.id)) {
				// Convertir IProviderDTO a IProviderListDTO para consistencia
				const selectedAsListDTO: IProviderListDTO = {
					...selectedProvider,
					syncStatus: {
						status: NetsuiteSyncStatusEnum.PendingSynchronization,
						netSuiteId: undefined,
						canExecuteSync: false,
						detailUrlInNetsuite: undefined,
					},
				};
				providersList = [selectedAsListDTO, ...providersList];
			}

			setProviders(providersList);
		} catch (error) {
			console.error('Error loading providers:', error);
		} finally {
			setProvidersLoading(false);
		}
	};

	// useEffect para búsqueda de proveedores
	useEffect(() => {
		if (isEditing) {
			loadProviders(debouncedProviderSearch);
		}
	}, [debouncedProviderSearch, isEditing]);

	useEffect(() => {
		const fetchData = async () => {
			if (!id) return;

			try {
				setLoading(true);
				setError(null);

				if (isCreateMode) {
					// Modo creación: inicializar con servicio vacío y cargar datos de apoyo
					const [serviceTypesData, citiesData] = await Promise.all([getServiceTypes(), getCities()]);

					// Crear servicio vacío con estructura inicial
					const emptyService: IPrechargedService = {
						id: 0,
						name: '',
						observations: '',
						internalNotes: '',
						enable: true,
						availableInQuotation: true,
						forAllDestinations: false,
						serviceType: 0,
						options: [],
						destinationCodes: [],
						provider: undefined,
						fares: [
							{
								id: 0,
								travelFrom: '',
								travelTo: '',
								bookingFrom: '',
								bookingTo: '',
								priceSingle: undefined,
								priceDbl: undefined,
								priceTpl: undefined,
								priceCpl: undefined,
								priceChild: undefined,
							},
						],
						lastUpdate: new Date().toISOString(),
						// Propiedades adicionales que puedan existir
						serviceTypeName: '',
						destinations: [],
					};

					setService(emptyService);
					setEditedService(emptyService);
					setIsEditing(true); // Activar modo edición por defecto
					setEditingFareId(0); // Activar edición de la primera tarifa automáticamente
					setServiceTypes(serviceTypesData.data);
					setCities(citiesData.data);
				} else {
					// Modo edición: cargar servicio existente
					const [serviceData, serviceTypesData, citiesData] = await Promise.all([
						getPrechargedServiceById(parseInt(id, 10)),
						getServiceTypes(),
						getCities(),
					]);

					setService(serviceData);
					setServiceTypes(serviceTypesData.data);
					setCities(citiesData.data);

					// Si el servicio tiene un provider asignado, cargar sus detalles
					if (serviceData.provider) {
						try {
							const providerData = await getProviderById(serviceData.provider);
							setSelectedProvider(providerData.data);
						} catch (providerError) {
							console.error('Error loading provider details:', providerError);
							// No setear error general, solo log del error del provider
						}
					}
				}
			} catch (err) {
				console.error('Error loading data:', err);
				setError('Error al cargar los datos. Por favor, intente nuevamente.');
			} finally {
				setLoading(false);
			}
		};

		fetchData();
	}, [id, isCreateMode]);

	const handleGoBack = () => {
		// En modo creación, confirmar si hay cambios sin guardar
		if (
			isCreateMode &&
			editedService &&
			(editedService.name?.trim() ||
				editedService.observations?.trim() ||
				editedService.serviceType !== 0 ||
				(editedService.destinationCodes && editedService.destinationCodes.length > 0) ||
				(editedService.fares &&
					editedService.fares.some(
						(fare) =>
							fare.travelFrom ||
							fare.travelTo ||
							fare.bookingFrom ||
							fare.bookingTo ||
							fare.priceSingle !== undefined ||
							fare.priceDbl !== undefined ||
							fare.priceTpl !== undefined ||
							fare.priceCpl !== undefined ||
							fare.priceChild !== undefined,
					)))
		) {
			const confirmLeave = window.confirm('¿Está seguro que desea salir? Se perderán todos los cambios no guardados.');
			if (!confirmLeave) return;
		}

		// Preservar los filtros del estado de navegación
		const state = location.state as { filters?: any; page?: number } | undefined;
		history.push('/precharged-services', state);
	};

	const handleEditToggle = () => {
		if (isEditing) {
			// Cancelar edición - restaurar datos originales
			setEditedService(null);
			setIsEditing(false);
			setProviderSearchValue('');
			setProviders([]);
			// Restaurar el provider original si había uno
			if (service?.provider && service.provider !== selectedProvider?.id) {
				if (service.provider) {
					getProviderById(service.provider)
						.then((response) => {
							setSelectedProvider(response.data);
						})
						.catch(() => {
							setSelectedProvider(null);
						});
				} else {
					setSelectedProvider(null);
				}
			}
		} else {
			// Iniciar edición - copiar datos actuales
			setEditedService(service ? { ...service } : null);
			setIsEditing(true);
			// Cargar proveedores iniciales
			loadProviders('');
		}
	};

	const handleSaveChanges = async () => {
		if (!editedService || !id) return;

		try {
			setSavingChanges(true);

			if (isCreateMode) {
				// CREAR NUEVO SERVICIO

				// Validar campos obligatorios
				if (!editedService.name?.trim()) {
					throw new Error('El nombre del servicio es obligatorio');
				}
				if (!editedService.serviceType || editedService.serviceType === 0) {
					throw new Error('Debe seleccionar un tipo de servicio');
				}
				if (
					!editedService.forAllDestinations &&
					(!editedService.destinationCodes || editedService.destinationCodes.length === 0)
				) {
					throw new Error('Debe seleccionar al menos un destino o marcar "Todos los destinos"');
				}

				// Validar tarifas
				if (!editedService.fares || editedService.fares.length === 0) {
					throw new Error('Debe agregar al menos una tarifa');
				}

				// Validar que cada tarifa tenga los datos mínimos
				for (let i = 0; i < editedService.fares.length; i++) {
					const fare = editedService.fares[i];
					if (!fare.travelFrom || !fare.travelTo) {
						throw new Error(`Tarifa ${i + 1}: Las fechas de viaje son obligatorias`);
					}
					if (!fare.bookingFrom || !fare.bookingTo) {
						throw new Error(`Tarifa ${i + 1}: Las fechas de reserva son obligatorias`);
					}
					// Verificar que al menos un precio esté definido
					const hasPrices =
						fare.priceSingle !== undefined ||
						fare.priceDbl !== undefined ||
						fare.priceTpl !== undefined ||
						fare.priceCpl !== undefined ||
						fare.priceChild !== undefined;
					if (!hasPrices) {
						throw new Error(`Tarifa ${i + 1}: Debe definir al menos un precio`);
					}
				}

				// Preparar payload - asegurar que tarifas tengan id: 0
				const servicePayload = {
					...editedService,
					id: 0, // Confirmar que es 0 para nuevo servicio
					fares:
						editedService.fares?.map((fare) => ({
							...fare,
							id: 0, // Todas las tarifas nuevas con 0
						})) || [],
				};

				// POST para crear
				const newServiceId = await createPrechargedService(servicePayload);

				// Después de crear exitosamente, cargar el servicio completo
				const newService = await getPrechargedServiceById(newServiceId);

				// Actualizar estados
				setService(newService);
				setEditedService(null);
				setIsEditing(false);

				// Cambiar URL sin recargar página
				history.replace(`/precharged-services/${newServiceId}`, location.state);

				setSaveAlert({
					show: true,
					severity: 'success',
					message: 'Servicio creado correctamente',
				});
			} else {
				// ACTUALIZAR SERVICIO EXISTENTE (código original)

				// Preparar el payload con el ID del servicio
				const servicePayload = {
					...editedService,
					id: parseInt(id, 10),
				};

				// Hacer el PUT para actualizar el servicio
				await updatePrechargedService(parseInt(id, 10), servicePayload);

				// Después del PUT exitoso, volver a cargar el servicio con GET
				const updatedService = await getPrechargedServiceById(parseInt(id, 10));

				setService(updatedService);
				setEditedService(null);
				setIsEditing(false);
				setSaveAlert({
					show: true,
					severity: 'success',
					message: 'Servicio actualizado correctamente',
				});
			}
		} catch (error) {
			console.error('Error saving changes:', error);
			setSaveAlert({
				show: true,
				severity: 'error',
				message: isCreateMode
					? `Error al crear el servicio: ${error instanceof Error ? error.message : 'Por favor, intente nuevamente.'}`
					: 'Error al guardar los cambios. Por favor, intente nuevamente.',
			});
		} finally {
			setSavingChanges(false);
		}
	};

	const handleFieldChange = (field: keyof IPrechargedService, value: any) => {
		if (!editedService) return;

		setEditedService({
			...editedService,
			[field]: value,
		});
	};

	const handleCloseSaveAlert = () => {
		setSaveAlert({ show: false, severity: 'success', message: '' });
	};

	// Helper para manejar los destinos seleccionados
	const getSelectedCities = () => {
		if (!editedService?.destinationCodes) return [];
		return cities.filter((city) => editedService.destinationCodes.includes(city.code));
	};

	const handleDestinationChange = (selectedCities: ICity[]) => {
		const destinationCodes = selectedCities.map((city) => city.code);
		handleFieldChange('destinationCodes', destinationCodes);
	};

	// Helper para obtener todas las opciones disponibles
	const getAllServiceOptions = () => {
		return Object.entries(SERVICE_OPTIONS_MAP).map(([id, name]) => ({
			id: Number(id),
			name,
		}));
	};

	// Helper para obtener las opciones seleccionadas
	const getSelectedOptions = () => {
		if (!editedService?.options) return [];
		const allOptions = getAllServiceOptions();
		return allOptions.filter((option) => editedService.options?.includes(option.id));
	};

	const handleOptionsChange = (selectedOptions: { id: number; name: string }[]) => {
		const optionIds = selectedOptions.map((option) => option.id);
		handleFieldChange('options', optionIds);
	};

	// Helper para encontrar proveedor en la lista de búsqueda
	const getSelectedProviderFromList = (): IProviderListDTO | null => {
		if (!selectedProvider?.id) return null;
		return providers.find((p) => p.id === selectedProvider.id) || null;
	};

	// Función para manejar el cambio de provider
	const handleProviderChange = async (providerId: string | null) => {
		handleFieldChange('provider', providerId);

		if (providerId) {
			try {
				const providerData = await getProviderById(providerId);
				setSelectedProvider(providerData.data);
			} catch (error) {
				console.error('Error loading provider details:', error);
				setSelectedProvider(null);
			}
		} else {
			setSelectedProvider(null);
		}
	};

	// Funciones para manejar edición de tarifas
	const handleFareEdit = (fareId: number) => {
		setEditingFareId(fareId);
	};

	const handleFareSave = async (fareId: number) => {
		if (!editedService || !id) return;

		try {
			const fare = editedService.fares?.find((f) => f.id === fareId);
			if (!fare) return;

			// Preparar el payload completo del servicio
			let servicesToSave = {
				...editedService,
				id: parseInt(id, 10), // Incluir el ID del servicio en el payload
			};

			if (fareId < 0) {
				// Es una nueva tarifa, cambiar ID temporal negativo por 0 en el payload
				const updatedFares = editedService.fares?.map((f) => (f.id === fareId ? { ...f, id: 0 } : f)) || [];
				servicesToSave = {
					...servicesToSave,
					fares: updatedFares,
				};
			}
			// Si es una tarifa existente, ya está incluida en editedService.fares

			// Hacer PUT del servicio completo
			await updatePrechargedService(parseInt(id, 10), servicesToSave);

			// Recargar el servicio completo después de guardar
			const updatedService = await getPrechargedServiceById(parseInt(id, 10));
			setService(updatedService);
			setEditedService({ ...updatedService });
			setEditingFareId(null);

			setSaveAlert({
				show: true,
				severity: 'success',
				message: 'Tarifa guardada correctamente',
			});
		} catch (error) {
			console.error('Error saving fare:', error);
			setSaveAlert({
				show: true,
				severity: 'error',
				message: 'Error al guardar la tarifa. Por favor, intente nuevamente.',
			});
		}
	};

	const handleFareCancel = (fareId: number) => {
		setEditingFareId(null);

		if (fareId < 0) {
			// Es una nueva tarifa, eliminarla del estado
			if (editedService?.fares) {
				const updatedFares = editedService.fares.filter((fare) => fare.id !== fareId);
				setEditedService({
					...editedService,
					fares: updatedFares,
				});
			}
		} else {
			// Restaurar los valores originales de una tarifa existente
			if (service?.fares && editedService?.fares) {
				const originalFare = service.fares.find((f) => f.id === fareId);
				if (originalFare) {
					const updatedFares = editedService.fares.map((fare) => (fare.id === fareId ? { ...originalFare } : fare));
					setEditedService({
						...editedService,
						fares: updatedFares,
					});
				}
			}
		}
	};

	const handleFareFieldChange = (fareId: number, field: keyof IPrechargedServiceFare, value: any) => {
		if (!editedService?.fares) return;

		const updatedFares = editedService.fares.map((fare) => (fare.id === fareId ? { ...fare, [field]: value } : fare));

		setEditedService({
			...editedService,
			fares: updatedFares,
		});
	};

	// Función para agregar nueva tarifa
	const handleAddNewFare = () => {
		if (!editedService) return;

		// Generar un ID temporal negativo para las nuevas tarifas
		const existingNegativeIds = editedService.fares?.map((f) => f.id).filter((id) => id < 0) || [];
		const newId = existingNegativeIds.length > 0 ? Math.min(...existingNegativeIds) - 1 : -1;

		const newFare: IPrechargedServiceFare = {
			id: newId,
			travelFrom: '',
			travelTo: '',
			bookingFrom: '',
			bookingTo: '',
			priceSingle: undefined,
			priceDbl: undefined,
			priceTpl: undefined,
			priceCpl: undefined,
			priceChild: undefined,
		};

		const updatedFares = [...(editedService.fares || []), newFare];

		setEditedService({
			...editedService,
			fares: updatedFares,
		});

		// Activar edición de la nueva tarifa inmediatamente
		setEditingFareId(newId);
	};

	// Función para eliminar una tarifa
	const handleDeleteFare = async (fareId: number) => {
		if (!editedService?.fares || !id) return;

		try {
			if (fareId < 0) {
				// Es una nueva tarifa que aún no se ha guardado, solo eliminar del estado local
				const updatedFares = editedService.fares.filter((fare) => fare.id !== fareId);

				setEditedService({
					...editedService,
					fares: updatedFares,
				});

				// Si estaba editando esta tarifa, cancelar la edición
				if (editingFareId === fareId) {
					setEditingFareId(null);
				}
			} else {
				// Es una tarifa existente, eliminar del array y hacer PUT del servicio completo
				const updatedFares = editedService.fares.filter((fare) => fare.id !== fareId);
				const servicesToSave = {
					...editedService,
					id: parseInt(id, 10), // Incluir el ID del servicio en el payload
					fares: updatedFares,
				};

				// Hacer PUT del servicio completo sin la tarifa eliminada
				await updatePrechargedService(parseInt(id, 10), servicesToSave);

				// Recargar el servicio completo después de eliminar
				const updatedService = await getPrechargedServiceById(parseInt(id, 10));
				setService(updatedService);
				setEditedService({ ...updatedService });

				// Si estaba editando esta tarifa, cancelar la edición
				if (editingFareId === fareId) {
					setEditingFareId(null);
				}

				setSaveAlert({
					show: true,
					severity: 'success',
					message: 'Tarifa eliminada correctamente',
				});
			}
		} catch (error) {
			console.error('Error deleting fare:', error);
			setSaveAlert({
				show: true,
				severity: 'error',
				message: 'Error al eliminar la tarifa. Por favor, intente nuevamente.',
			});
		}
	};

	const formatDate = (dateString: string | undefined | null) => {
		if (!dateString) {
			return '-';
		}
		try {
			return new Date(dateString).toLocaleDateString('es-ES', {
				year: 'numeric',
				month: '2-digit',
				day: '2-digit',
			});
		} catch {
			return dateString;
		}
	};

	const formatCurrency = (amount: number | undefined | null) => {
		if (amount === undefined || amount === null) {
			return '-';
		}
		return `$${amount.toLocaleString('es-ES', { minimumFractionDigits: 2 })}`;
	};

	// Helper para convertir fecha a string de forma segura
	const dateToString = (date: Date | null): string | null => {
		if (!date || isNaN(date.getTime())) {
			return null;
		}
		return date.toISOString().split('T')[0];
	};

	if (loading) {
		return (
			<Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
				<CircularProgress />
			</Box>
		);
	}

	if (error) {
		return (
			<Box>
				<Button startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mb: 2 }}>
					Volver al listado
				</Button>
				<Alert severity="error">{error}</Alert>
			</Box>
		);
	}

	if (!service) {
		return (
			<Box>
				<Button startIcon={<ArrowBack />} onClick={handleGoBack} sx={{ mb: 2 }}>
					Volver al listado
				</Button>
				<Alert severity="warning">Servicio no encontrado</Alert>
			</Box>
		);
	}

	return (
		<>
			{/* Header mejorado */}
			<Box mb={2}>
				<Stack direction="row" alignItems="center" spacing={2} mb={2} justifyContent="space-between">
					<Stack direction="row" alignItems="center" spacing={2}>
						<Button
							variant="outlined"
							startIcon={<ArrowBack />}
							onClick={handleGoBack}
							size="small"
							sx={{
								borderRadius: 2,
								px: 2,
								py: 1,
							}}
						>
							Volver al listado
						</Button>
						<Typography variant="h5" component="h1" fontWeight="600" color="primary.main">
							{isCreateMode ? 'Crear Nuevo Servicio' : 'Detalle del Servicio'}
						</Typography>
					</Stack>

					{canEdit && (
						<Stack direction="row" spacing={1}>
							{isCreateMode ? (
								<>
									<Button
										variant="contained"
										startIcon={<Save />}
										onClick={handleSaveChanges}
										disabled={savingChanges}
										size="small"
										color="primary"
									>
										{savingChanges ? 'Creando...' : 'Crear Servicio'}
									</Button>
									<Button variant="outlined" startIcon={<Close />} onClick={handleGoBack} size="small">
										Cancelar
									</Button>
								</>
							) : isEditing ? (
								<>
									<Button
										variant="contained"
										startIcon={<Save />}
										onClick={handleSaveChanges}
										disabled={savingChanges}
										size="small"
										color="success"
									>
										{savingChanges ? 'Guardando...' : 'Guardar'}
									</Button>
									<Button variant="outlined" startIcon={<Close />} onClick={handleEditToggle} size="small">
										Cancelar
									</Button>
								</>
							) : (
								<Button variant="contained" startIcon={<Edit />} onClick={handleEditToggle} size="small">
									Editar
								</Button>
							)}
						</Stack>
					)}
				</Stack>
			</Box>

			{/* Header del servicio con información principal */}
			<Card
				elevation={1}
				sx={{
					mb: 2,
					borderRadius: 2,
					background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
					color: 'white',
					position: 'relative',
					overflow: 'hidden',
				}}
			>
				<CardContent sx={{ p: 3 }}>
					<Grid container spacing={2} alignItems="center">
						<Grid item xs={12} md={8}>
							<Stack spacing={1.5}>
								<Box display="flex" alignItems="center" gap={1.5}>
									<Avatar
										sx={{
											bgcolor: 'rgba(255,255,255,0.2)',
											width: 36,
											height: 36,
										}}
									>
										<Category fontSize="small" />
									</Avatar>
									<Box sx={{ flex: 1 }}>
										<Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.7rem' }}>
											ID #{service.id}
										</Typography>
										{isEditing ? (
											<TextField
												value={editedService?.name || ''}
												onChange={(e) => handleFieldChange('name', e.target.value)}
												variant="outlined"
												size="small"
												sx={{
													mt: 0.5,
													'& .MuiOutlinedInput-root': {
														color: 'white',
														'& fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
														'&:hover fieldset': { borderColor: 'rgba(255,255,255,0.8)' },
														'&.Mui-focused fieldset': { borderColor: 'white' },
													},
													'& .MuiInputBase-input': {
														color: 'white',
														fontWeight: 600,
														fontSize: '1.25rem',
													},
												}}
												fullWidth
											/>
										) : (
											<Typography variant="h6" component="h1" fontWeight="600" sx={{ lineHeight: 1.2 }}>
												{service.name}
											</Typography>
										)}
									</Box>
								</Box>

								{service.provider && (
									<Box display="flex" alignItems="center" gap={1}>
										<Business sx={{ fontSize: 16, opacity: 0.8 }} />
										<Typography variant="body2" sx={{ opacity: 0.9 }}>
											<strong>Proveedor:</strong> {service.provider}
										</Typography>
									</Box>
								)}
							</Stack>
						</Grid>

						<Grid item xs={12} md={4}>
							<Stack spacing={1.5} alignItems={{ xs: 'flex-start', md: 'flex-end' }}>
								{isEditing ? (
									<>
										<FormControlLabel
											control={
												<Switch
													checked={editedService?.enable || false}
													onChange={(e) => handleFieldChange('enable', e.target.checked)}
													sx={{
														'& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' },
														'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#4caf50' },
														'& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' },
													}}
												/>
											}
											label={
												<Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
													{editedService?.enable ? 'Servicio Activo' : 'Servicio Inactivo'}
												</Typography>
											}
											sx={{ color: 'white' }}
										/>
										<FormControlLabel
											control={
												<Switch
													checked={editedService?.availableInQuotation !== false} // Por defecto true si no existe
													onChange={(e) => handleFieldChange('availableInQuotation', e.target.checked)}
													sx={{
														'& .MuiSwitch-switchBase.Mui-checked': { color: '#2196f3' },
														'& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: '#2196f3' },
														'& .MuiSwitch-track': { backgroundColor: 'rgba(255,255,255,0.3)' },
													}}
												/>
											}
											label={
												<Typography variant="caption" sx={{ color: 'white', fontWeight: 'bold' }}>
													{editedService?.availableInQuotation !== false
														? 'Disponible en cotizador'
														: 'No disponible en cotizador'}
												</Typography>
											}
											sx={{ color: 'white' }}
										/>
									</>
								) : (
									<>
										<Chip
											icon={service.enable ? <CheckCircle /> : <Cancel />}
											label={service.enable ? 'Servicio Activo' : 'Servicio Inactivo'}
											color={service.enable ? 'success' : 'error'}
											size="small"
											sx={{
												fontWeight: 'bold',
												px: 1.5,
												py: 1,
												fontSize: '0.75rem',
												'& .MuiChip-icon': { fontSize: 16 },
											}}
										/>
										<Chip
											icon={service.availableInQuotation !== false ? <CheckCircle /> : <Cancel />}
											label={
												service.availableInQuotation !== false
													? 'Disponible en cotizador'
													: 'No disponible en cotizador'
											}
											color={service.availableInQuotation !== false ? 'info' : 'warning'}
											size="small"
											sx={{
												fontWeight: 'bold',
												px: 1.5,
												py: 1,
												fontSize: '0.75rem',
												'& .MuiChip-icon': { fontSize: 16 },
											}}
										/>
									</>
								)}

								<Box display="flex" alignItems="center" gap={1} sx={{ opacity: 0.9 }}>
									<AccessTime sx={{ fontSize: 16 }} />
									<Typography variant="caption">Actualizado: {formatDate(service.lastUpdate)}</Typography>
								</Box>
							</Stack>
						</Grid>
					</Grid>
				</CardContent>
			</Card>

			<Grid container spacing={2}>
				{/* Información general */}
				<Grid item xs={12} md={4}>
					<Card elevation={1} sx={{ borderRadius: 2, height: 'fit-content' }}>
						<CardContent sx={{ p: 2 }}>
							<Stack spacing={2}>
								<Box display="flex" alignItems="center" gap={1.5}>
									<Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
										<Category fontSize="small" />
									</Avatar>
									<Typography variant="subtitle1" fontWeight="600">
										Información General
									</Typography>
								</Box>
								<Divider />

								<Stack spacing={1.5}>
									<Box>
										<Typography variant="caption" color="text.secondary" gutterBottom>
											Tipo de Servicio
										</Typography>
										{isEditing ? (
											<FormControl size="small" fullWidth>
												<Select
													value={editedService?.serviceType || ''}
													onChange={(e) => handleFieldChange('serviceType', Number(e.target.value))}
													variant="outlined"
												>
													{serviceTypes.map((type) => (
														<MenuItem key={type.id} value={type.id}>
															{type.name}
														</MenuItem>
													))}
												</Select>
											</FormControl>
										) : (
											<Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
												<Chip
													label={getServiceTypeName(serviceTypes, service.serviceType)}
													variant="outlined"
													color="primary"
													icon={<Category />}
													size="small"
												/>
											</Box>
										)}
									</Box>

									<Box>
										<Typography variant="caption" color="text.secondary" gutterBottom>
											Cobertura de Destinos
										</Typography>
										{isEditing ? (
											<Stack spacing={1}>
												<FormControlLabel
													control={
														<Switch
															checked={editedService?.forAllDestinations || false}
															onChange={(e) => handleFieldChange('forAllDestinations', e.target.checked)}
															size="small"
														/>
													}
													label="Aplicar a todos los destinos"
													sx={{ fontSize: '0.75rem' }}
												/>
												{!editedService?.forAllDestinations && (
													<Autocomplete
														multiple
														size="small"
														options={cities}
														getOptionLabel={(option) => `${option.name} (${option.code})`}
														value={getSelectedCities()}
														onChange={(_, newValue) => handleDestinationChange(newValue)}
														renderInput={(params) => (
															<TextField {...params} placeholder="Seleccionar destinos..." variant="outlined" />
														)}
														renderTags={(value, getTagProps) =>
															value.map((option, index) => {
																const { key, ...chipProps } = getTagProps({ index });
																return (
																	<Chip
																		key={key}
																		variant="outlined"
																		label={`${option.name} (${option.code})`}
																		size="small"
																		{...chipProps}
																	/>
																);
															})
														}
														sx={{ mt: 0.5 }}
													/>
												)}
											</Stack>
										) : (
											<Box display="flex" alignItems="center" gap={0.5} flexWrap="wrap">
												{service.forAllDestinations ? (
													<Chip
														icon={<Public />}
														label="Todos los destinos"
														color="primary"
														variant="filled"
														size="small"
														sx={{ fontWeight: 'bold' }}
													/>
												) : (
													<>
														{service.destinationCodes?.map((destination) => (
															<Chip
																key={destination}
																icon={<LocationOn />}
																label={destination}
																variant="outlined"
																color="secondary"
																size="small"
															/>
														)) || (
															<Typography variant="body2" color="text.secondary">
																Sin destinos específicos
															</Typography>
														)}
													</>
												)}
											</Box>
										)}
									</Box>
									<Box>
										<Typography variant="caption" color="text.secondary" gutterBottom>
											Opciones disponibles
										</Typography>
										{isEditing ? (
											<Autocomplete
												multiple
												size="small"
												options={getAllServiceOptions()}
												getOptionLabel={(option) => option.name}
												value={getSelectedOptions()}
												onChange={(_, newValue) => handleOptionsChange(newValue)}
												renderInput={(params) => (
													<TextField {...params} placeholder="Seleccionar opciones..." variant="outlined" />
												)}
												renderTags={(value, getTagProps) =>
													value.map((option, index) => {
														const { key, ...chipProps } = getTagProps({ index });
														return (
															<Chip
																key={key}
																variant="outlined"
																label={option.name}
																size="small"
																color="info"
																icon={<Settings />}
																{...chipProps}
															/>
														);
													})
												}
												sx={{ mt: 0.5 }}
											/>
										) : (
											<Box display="flex" gap={0.5} flexWrap="wrap">
												{service.options?.map((option) => (
													<Chip
														key={option}
														label={getServiceOptionName(option)}
														variant="outlined"
														color="info"
														icon={<Settings />}
														size="small"
														sx={{ fontWeight: 'medium' }}
													/>
												)) || (
													<Typography variant="body2" color="text.secondary">
														Sin opciones disponibles
													</Typography>
												)}
											</Box>
										)}
									</Box>

									<Box>
										<Typography variant="caption" color="text.secondary" gutterBottom>
											Proveedor
										</Typography>
										{isEditing ? (
											<Autocomplete
												size="small"
												freeSolo
												options={providers}
												getOptionLabel={(option) =>
													typeof option === 'string' ? option : `${option.fantasyName} (${option.id})`
												}
												value={getSelectedProviderFromList()}
												onChange={(event, newValue) => {
													if (newValue && typeof newValue === 'object') {
														handleProviderChange(newValue.id);
													} else {
														handleProviderChange(null);
													}
												}}
												inputValue={providerSearchValue}
												onInputChange={(event, newInputValue) => {
													setProviderSearchValue(newInputValue);
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Proveedor"
														placeholder="Buscar proveedor..."
														variant="outlined"
														InputProps={{
															...params.InputProps,
															startAdornment: (
																<Box sx={{ display: 'flex', alignItems: 'center', ml: 1 }}>
																	<Business sx={{ fontSize: 16, color: 'text.secondary' }} />
																</Box>
															),
															endAdornment: (
																<Box sx={{ display: 'flex', alignItems: 'center' }}>
																	{providersLoading && <CircularProgress color="inherit" size={16} />}
																	{params.InputProps.endAdornment}
																</Box>
															),
														}}
													/>
												)}
												renderOption={(props, option) => (
													<Box component="li" {...props}>
														<Stack spacing={0.5}>
															<Typography variant="body2" fontWeight="medium">
																{option.fantasyName}
															</Typography>
															<Typography variant="caption" color="text.secondary">
																{option.businessName} • ID: {option.id}
															</Typography>
														</Stack>
													</Box>
												)}
												noOptionsText={
													providerSearchValue.length > 0
														? 'No se encontraron proveedores'
														: 'Escribe para buscar proveedores'
												}
												loadingText="Buscando proveedores..."
												sx={{ width: '100%' }}
											/>
										) : (
											<Box>
												{selectedProvider ? (
													<Box>
														<Chip
															label={selectedProvider.fantasyName}
															variant="outlined"
															color="secondary"
															icon={<Business />}
															size="small"
															sx={{ fontWeight: 'medium', mb: 1 }}
														/>
														<Paper
															variant="outlined"
															sx={{
																p: 1.5,
																backgroundColor: 'grey.50',
																borderRadius: 1.5,
																mt: 1,
															}}
														>
															<Stack spacing={0.5}>
																<Typography variant="caption" color="text.primary" fontWeight="600">
																	{selectedProvider.fantasyName}
																</Typography>
																<Typography variant="caption" color="text.secondary">
																	{selectedProvider.businessName}
																</Typography>
																{selectedProvider.country && (
																	<Typography variant="caption" color="text.secondary">
																		📍 {selectedProvider.country.name}
																	</Typography>
																)}
																{selectedProvider.phone && (
																	<Typography variant="caption" color="text.secondary">
																		📞 {selectedProvider.phone}
																	</Typography>
																)}
																{selectedProvider.email && (
																	<Typography variant="caption" color="text.secondary">
																		📧 {selectedProvider.email}
																	</Typography>
																)}
															</Stack>
														</Paper>
													</Box>
												) : (
													<Typography variant="body2" color="text.secondary">
														Sin proveedor asignado
													</Typography>
												)}
											</Box>
										)}
									</Box>
								</Stack>
							</Stack>
						</CardContent>
					</Card>
				</Grid>

				{/* Columna derecha: Observaciones y Notas Internas */}
				<Grid item xs={12} md={8}>
					<Stack spacing={2}>
						{/* Observaciones */}
						{(service.observations || isEditing) && (
							<Card elevation={1} sx={{ borderRadius: 2, height: 'fit-content' }}>
								<CardContent sx={{ p: 2 }}>
									<Stack spacing={1.5}>
										<Box display="flex" alignItems="center" gap={1.5}>
											<Avatar sx={{ bgcolor: 'warning.main', width: 32, height: 32 }}>
												<Description fontSize="small" />
											</Avatar>
											<Typography variant="subtitle1" fontWeight="600">
												Descripción y Observaciones
											</Typography>
										</Box>
										<Divider />

										{isEditing ? (
											<TextField
												value={editedService?.observations || ''}
												onChange={(e) => handleFieldChange('observations', e.target.value)}
												multiline
												rows={4}
												fullWidth
												variant="outlined"
												size="small"
												placeholder="Escriba las observaciones aquí..."
												sx={{
													'& .MuiOutlinedInput-root': {
														backgroundColor: 'grey.50',
														borderRadius: 1.5,
													},
												}}
											/>
										) : (
											<Paper
												variant="outlined"
												sx={{
													p: 1.5,
													backgroundColor: 'grey.50',
													borderRadius: 1.5,
													maxHeight: 200,
													overflow: 'auto',
												}}
											>
												<Typography
													variant="body2"
													component="pre"
													sx={{
														whiteSpace: 'pre-wrap',
														fontFamily: 'inherit',
														lineHeight: 1.6,
													}}
												>
													{service.observations}
												</Typography>
											</Paper>
										)}
									</Stack>
								</CardContent>
							</Card>
						)}

						{/* Notas Internas */}
						{(service.internalNotes || isEditing) && (
							<Card
								elevation={1}
								sx={{
									borderRadius: 2,
									height: 'fit-content',
									backgroundColor: '#fffbf0', // Fondo amarillo claro
									border: '1px solid #ffc107',
									position: 'relative',
									'&::before': {
										content: '""',
										position: 'absolute',
										top: -2,
										left: 20,
										width: 40,
										height: 20,
										backgroundColor: '#ffc107',
										borderRadius: '0 0 50% 50%',
										opacity: 0.3,
									},
								}}
							>
								<CardContent sx={{ p: 2 }}>
									<Stack spacing={1.5}>
										<Box display="flex" alignItems="center" gap={1.5}>
											<Avatar sx={{ bgcolor: '#ff9800', width: 32, height: 32 }}>
												<Description fontSize="small" />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight="600" color="text.primary">
													Notas Internas
												</Typography>
												<Typography variant="caption" color="warning.dark" fontWeight="500">
													⚠️ Información importante para agentes
												</Typography>
											</Box>
										</Box>
										<Divider sx={{ borderColor: '#ffc107' }} />

										{isEditing ? (
											<TextField
												value={editedService?.internalNotes || ''}
												onChange={(e) => handleFieldChange('internalNotes', e.target.value)}
												multiline
												rows={3}
												fullWidth
												variant="outlined"
												size="small"
												placeholder="Escriba las notas internas aquí..."
												sx={{
													'& .MuiOutlinedInput-root': {
														backgroundColor: '#fff9c4',
														borderRadius: 1.5,
														'& fieldset': {
															borderColor: '#ffc107',
														},
														'&:hover fieldset': {
															borderColor: '#ff9800',
														},
														'&.Mui-focused fieldset': {
															borderColor: '#ff9800',
														},
													},
												}}
											/>
										) : (
											<Paper
												variant="outlined"
												sx={{
													p: 1.5,
													backgroundColor: '#fff9c4',
													borderRadius: 1.5,
													maxHeight: 150,
													overflow: 'auto',
													borderColor: '#ffc107',
													boxShadow: 'inset 0 1px 3px rgba(255, 193, 7, 0.2)',
												}}
											>
												<Typography
													variant="body2"
													component="pre"
													sx={{
														whiteSpace: 'pre-wrap',
														fontFamily: 'inherit',
														lineHeight: 1.6,
														color: 'text.primary',
														fontWeight: 500,
													}}
												>
													{service.internalNotes}
												</Typography>
											</Paper>
										)}
									</Stack>
								</CardContent>
							</Card>
						)}
					</Stack>
				</Grid>

				{/* Tarifas */}
				{service.fares && service.fares.length > 0 && (
					<Grid item xs={12}>
						<Card elevation={1} sx={{ borderRadius: 2 }}>
							<CardContent sx={{ p: 2 }}>
								<Stack spacing={2}>
									<Box display="flex" alignItems="center" justifyContent="space-between">
										<Box display="flex" alignItems="center" gap={1.5}>
											<Avatar sx={{ bgcolor: 'success.main', width: 32, height: 32 }}>
												<AttachMoney fontSize="small" />
											</Avatar>
											<Box>
												<Typography variant="subtitle1" fontWeight="600">
													Tarifas y Precios
												</Typography>
												<Typography variant="caption" color="text.secondary">
													{(isEditing ? editedService?.fares?.length : service.fares.length) || 0} tarifa
													{((isEditing ? editedService?.fares?.length : service.fares.length) || 0) !== 1
														? 's'
														: ''}{' '}
													disponible
													{((isEditing ? editedService?.fares?.length : service.fares.length) || 0) !== 1 ? 's' : ''}
												</Typography>
											</Box>
										</Box>

										{isEditing && canEdit && (
											<Button
												variant="contained"
												startIcon={<Add />}
												onClick={handleAddNewFare}
												size="small"
												color="primary"
												sx={{
													borderRadius: 2,
													px: 2,
													py: 1,
												}}
											>
												Agregar Tarifa
											</Button>
										)}
									</Box>
									<Divider />

									<TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 1.5 }}>
										<Table size="small">
											<TableHead>
												<TableRow sx={{ bgcolor: 'grey.50' }}>
													<TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>ID</TableCell>
													<TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" gap={0.5}>
															<CalendarToday sx={{ fontSize: 14 }} />
															Período de Viaje
														</Box>
													</TableCell>
													<TableCell sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" gap={0.5}>
															<CalendarToday sx={{ fontSize: 14 }} />
															Período de Reserva
														</Box>
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
															<AttachMoney sx={{ fontSize: 14 }} />
															Precio Individual
														</Box>
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
															<AttachMoney sx={{ fontSize: 14 }} />
															Precio Doble
														</Box>
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
															<AttachMoney sx={{ fontSize: 14 }} />
															Precio Triple
														</Box>
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
															<AttachMoney sx={{ fontSize: 14 }} />
															Precio Cuádruple
														</Box>
													</TableCell>
													<TableCell align="right" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
														<Box display="flex" alignItems="center" justifyContent="flex-end" gap={0.5}>
															<AttachMoney sx={{ fontSize: 14 }} />
															Precio Niño
														</Box>
													</TableCell>
													{canEdit && (
														<TableCell align="center" sx={{ fontWeight: 'bold', fontSize: '0.75rem', py: 1 }}>
															Acciones
														</TableCell>
													)}
												</TableRow>
											</TableHead>
											<TableBody>
												{(isEditing ? editedService?.fares : service.fares)?.map((fare) => {
													const isEditingThisFare = editingFareId === fare.id;
													return (
														<TableRow
															key={fare.id}
															hover
															sx={{
																'&:nth-of-type(odd)': {
																	bgcolor: 'action.hover',
																},
																bgcolor: isEditingThisFare ? 'rgba(25, 118, 210, 0.08)' : undefined,
															}}
														>
															<TableCell sx={{ py: 1 }}>
																<Chip
																	label={`#${fare?.id || '-'}`}
																	size="small"
																	color="primary"
																	variant="outlined"
																	sx={{ fontSize: '0.7rem' }}
																/>
															</TableCell>

															{/* Período de Viaje */}
															<TableCell sx={{ py: 1, minWidth: 180 }}>
																{isEditingThisFare ? (
																	<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
																		<Stack spacing={1}>
																			<DatePicker
																				label="Desde"
																				value={fare.travelFrom ? new Date(fare.travelFrom) : null}
																				onChange={(newValue: Date | null) => {
																					const dateString = dateToString(newValue);
																					if (dateString) {
																						handleFareFieldChange(fare.id, 'travelFrom', dateString);
																					} else {
																						handleFareFieldChange(fare.id, 'travelFrom', '');
																					}
																				}}
																				inputFormat="dd/MM/yyyy"
																				renderInput={(params) => (
																					<TextField {...params} size="small" variant="outlined" />
																				)}
																			/>
																			<DatePicker
																				label="Hasta"
																				value={fare.travelTo ? new Date(fare.travelTo) : null}
																				onChange={(newValue: Date | null) => {
																					const dateString = dateToString(newValue);
																					if (dateString) {
																						handleFareFieldChange(fare.id, 'travelTo', dateString);
																					} else {
																						handleFareFieldChange(fare.id, 'travelTo', '');
																					}
																				}}
																				inputFormat="dd/MM/yyyy"
																				renderInput={(params) => (
																					<TextField {...params} size="small" variant="outlined" />
																				)}
																			/>
																		</Stack>
																	</LocalizationProvider>
																) : (
																	<Typography variant="caption" fontWeight="500">
																		{formatDate(fare?.travelFrom)} → {formatDate(fare?.travelTo)}
																	</Typography>
																)}
															</TableCell>

															{/* Período de Reserva */}
															<TableCell sx={{ py: 1, minWidth: 180 }}>
																{isEditingThisFare ? (
																	<LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
																		<Stack spacing={1}>
																			<DatePicker
																				label="Desde"
																				value={fare.bookingFrom ? new Date(fare.bookingFrom) : null}
																				onChange={(newValue: Date | null) => {
																					const dateString = dateToString(newValue);
																					if (dateString) {
																						handleFareFieldChange(fare.id, 'bookingFrom', dateString);
																					} else {
																						handleFareFieldChange(fare.id, 'bookingFrom', '');
																					}
																				}}
																				inputFormat="dd/MM/yyyy"
																				renderInput={(params) => (
																					<TextField {...params} size="small" variant="outlined" />
																				)}
																			/>
																			<DatePicker
																				label="Hasta"
																				value={fare.bookingTo ? new Date(fare.bookingTo) : null}
																				onChange={(newValue: Date | null) => {
																					const dateString = dateToString(newValue);
																					if (dateString) {
																						handleFareFieldChange(fare.id, 'bookingTo', dateString);
																					} else {
																						handleFareFieldChange(fare.id, 'bookingTo', '');
																					}
																				}}
																				inputFormat="dd/MM/yyyy"
																				renderInput={(params) => (
																					<TextField {...params} size="small" variant="outlined" />
																				)}
																			/>
																		</Stack>
																	</LocalizationProvider>
																) : (
																	<Typography variant="caption" fontWeight="500">
																		{formatDate(fare?.bookingFrom)} → {formatDate(fare?.bookingTo)}
																	</Typography>
																)}
															</TableCell>

															{/* Precio Individual */}
															<TableCell align="right" sx={{ py: 1 }}>
																{isEditingThisFare ? (
																	<TextField
																		type="number"
																		size="small"
																		value={fare.priceSingle || ''}
																		onChange={(e) =>
																			handleFareFieldChange(fare.id, 'priceSingle', parseFloat(e.target.value) || null)
																		}
																		variant="outlined"
																		sx={{ width: 100 }}
																		inputProps={{ step: 0.01, min: 0 }}
																	/>
																) : (
																	<Typography variant="subtitle2" color="primary.main" fontWeight="600">
																		{formatCurrency(fare?.priceSingle)}
																	</Typography>
																)}
															</TableCell>

															{/* Precio Doble */}
															<TableCell align="right" sx={{ py: 1 }}>
																{isEditingThisFare ? (
																	<TextField
																		type="number"
																		size="small"
																		value={fare.priceDbl || ''}
																		onChange={(e) =>
																			handleFareFieldChange(fare.id, 'priceDbl', parseFloat(e.target.value) || null)
																		}
																		variant="outlined"
																		sx={{ width: 100 }}
																		inputProps={{ step: 0.01, min: 0 }}
																	/>
																) : (
																	<Typography variant="subtitle2" color="primary.main" fontWeight="600">
																		{formatCurrency(fare?.priceDbl)}
																	</Typography>
																)}
															</TableCell>

															{/* Precio Triple */}
															<TableCell align="right" sx={{ py: 1 }}>
																{isEditingThisFare ? (
																	<TextField
																		type="number"
																		size="small"
																		value={fare.priceTpl || ''}
																		onChange={(e) =>
																			handleFareFieldChange(fare.id, 'priceTpl', parseFloat(e.target.value) || null)
																		}
																		variant="outlined"
																		sx={{ width: 100 }}
																		inputProps={{ step: 0.01, min: 0 }}
																	/>
																) : (
																	<Typography variant="subtitle2" color="primary.main" fontWeight="600">
																		{formatCurrency(fare?.priceTpl)}
																	</Typography>
																)}
															</TableCell>

															{/* Precio Cuádruple */}
															<TableCell align="right" sx={{ py: 1 }}>
																{isEditingThisFare ? (
																	<TextField
																		type="number"
																		size="small"
																		value={fare.priceCpl || ''}
																		onChange={(e) =>
																			handleFareFieldChange(fare.id, 'priceCpl', parseFloat(e.target.value) || null)
																		}
																		variant="outlined"
																		sx={{ width: 100 }}
																		inputProps={{ step: 0.01, min: 0 }}
																	/>
																) : (
																	<Typography variant="subtitle2" color="primary.main" fontWeight="600">
																		{formatCurrency(fare?.priceCpl)}
																	</Typography>
																)}
															</TableCell>

															{/* Precio Niño */}
															<TableCell align="right" sx={{ py: 1 }}>
																{isEditingThisFare ? (
																	<TextField
																		type="number"
																		size="small"
																		value={fare.priceChild || ''}
																		onChange={(e) =>
																			handleFareFieldChange(fare.id, 'priceChild', parseFloat(e.target.value) || null)
																		}
																		variant="outlined"
																		sx={{ width: 100 }}
																		inputProps={{ step: 0.01, min: 0 }}
																	/>
																) : (
																	<Typography variant="subtitle2" color="primary.main" fontWeight="600">
																		{formatCurrency(fare?.priceChild)}
																	</Typography>
																)}
															</TableCell>

															{/* Acciones */}
															{canEdit && (
																<TableCell align="center" sx={{ py: 1 }}>
																	{isEditingThisFare ? (
																		<Stack direction="row" spacing={0.5} justifyContent="center">
																			<IconButton
																				size="small"
																				onClick={() => handleFareSave(fare.id)}
																				color="success"
																				title="Guardar cambios"
																			>
																				<Save fontSize="small" />
																			</IconButton>
																			<IconButton
																				size="small"
																				onClick={() => handleFareCancel(fare.id)}
																				color="error"
																				title="Cancelar cambios"
																			>
																				<Close fontSize="small" />
																			</IconButton>
																		</Stack>
																	) : (
																		<Stack direction="row" spacing={0.5} justifyContent="center">
																			<IconButton
																				size="small"
																				onClick={() => handleFareEdit(fare.id)}
																				color="primary"
																				title="Editar tarifa"
																				disabled={!isEditing}
																			>
																				<Edit fontSize="small" />
																			</IconButton>
																			{isEditing && (
																				<IconButton
																					size="small"
																					onClick={() => handleDeleteFare(fare.id)}
																					color="error"
																					title="Eliminar tarifa"
																				>
																					<Delete fontSize="small" />
																				</IconButton>
																			)}
																		</Stack>
																	)}
																</TableCell>
															)}
														</TableRow>
													);
												})}
											</TableBody>
										</Table>
									</TableContainer>
								</Stack>
							</CardContent>
						</Card>
					</Grid>
				)}
			</Grid>

			{/* Snackbar para mensajes de guardado */}
			<Snackbar
				open={saveAlert.show}
				autoHideDuration={4000}
				onClose={handleCloseSaveAlert}
				anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
			>
				<Alert onClose={handleCloseSaveAlert} severity={saveAlert.severity} variant="filled" sx={{ width: '100%' }}>
					{saveAlert.message}
				</Alert>
			</Snackbar>
		</>
	);
};

export default PrechargedServiceDetail;
